import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Upgrade as UpgradeIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import axios from 'axios';

// 分割されたコンポーネントをインポート
import Header from '../../components/Header';
import ClientAutocomplete from '../../components/ClientAutocomplete';
import AnalysisChart from './ProductAnalysisDashboard/AnalysisChart';
import AnalysisKPICards from './ProductAnalysisDashboard/AnalysisKPICards';
import AnalysisPeriodSelector from './ProductAnalysisDashboard/AnalysisPeriodSelector';

const ProductAnalysisDashboard = () => {
  // 状態管理
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [chartType, setChartType] = useState('bar');
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  const [planLimitDialog, setPlanLimitDialog] = useState(null);

  // デフォルトを現在月に変更
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return {
      startDate: `${year}-${month}-01`,
      endDate: `${year}-${month}-${new Date(year, now.getMonth() + 1, 0).getDate()}`
    };
  };

  const [dateRange, setDateRange] = useState(getCurrentMonth());

  // データ取得
  useEffect(() => {
    fetchUserPlan();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchAnalysisData();
    }
  }, [selectedProduct, selectedClient, dateRange]);

  // ユーザープラン情報取得
  const fetchUserPlan = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPlan(response.data.plan);
    } catch (err) {
      console.error('プラン情報取得エラー:', err);
    }
  };

  // 製品データ取得
  const fetchProducts = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get('/api/products', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || [];
      setProducts(data);
      
      // デフォルト製品選択：アクティブな製品の最初の1つ
      const activeProducts = data.filter(product => 
        product && product.id && product.name && product.active !== false
      );
      
      if (activeProducts.length > 0) {
        setSelectedProduct(activeProducts[0]);
      } else {
        setError('利用可能な製品がありません。製品を登録してください。');
      }
    } catch (err) {
      setError('製品データの取得に失敗しました');
    }
  };

  // 分析データ取得（プラン制限対応版）
  const fetchAnalysisData = async (showRefreshLoader = false) => {
    if (!selectedProduct) return;
    
    if (showRefreshLoader) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const token = sessionStorage.getItem('token');
      
      const params = {
        productId: selectedProduct.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      
      if (selectedClient) {
        params.clientId = selectedClient.id;
      }
      
      const response = await axios.get('/api/analysis/product-monthly-summary', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      // バックエンドでエラーメッセージが返された場合の処理
      if (response.data && typeof response.data === 'string') {
        handlePlanLimitError(response.data);
        return;
      }
      
      setAnalysisData(response.data);
      setError('');
    } catch (err) {
      if (err.response && err.response.data && typeof err.response.data === 'string') {
        handlePlanLimitError(err.response.data);
      } else {
        setError('分析データの取得に失敗しました');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // プラン制限エラー処理
  const handlePlanLimitError = (errorMessage) => {
    if (errorMessage.includes('顧客フィルター')) {
      setPlanLimitDialog({
        type: 'CLIENT_FILTER',
        title: '顧客フィルター機能の制限',
        message: errorMessage,
        action: 'clear_client'
      });
      setSelectedClient(null); // 顧客選択をクリア
    } else if (errorMessage.includes('期間変更')) {
      setPlanLimitDialog({
        type: 'PERIOD_FILTER',
        title: '期間変更機能の制限',
        message: errorMessage,
        action: 'reset_period'
      });
      setDateRange(getCurrentMonth()); // 今月にリセット
    }
  };

  const handleRefresh = () => {
    fetchAnalysisData(true);
  };

  // 顧客選択処理（プラン制限対応版）
  const handleClientChange = (newClient) => {
    // フリープランで顧客を選択しようとした場合
    if (userPlan?.id === 1 && newClient) {
      setPlanLimitDialog({
        type: 'CLIENT_FILTER',
        title: '顧客フィルター機能の制限',
        message: '顧客フィルター機能はスタンダードプラン以上で利用可能です。',
        action: 'upgrade'
      });
      return;
    }
    
    setSelectedClient(newClient);
  };

  // 期間変更処理（プラン制限対応版）
  const handleDateRangeChange = (newDateRange) => {
    // フリープランで今月以外を選択しようとした場合
    if (userPlan?.id === 1) {
      const currentMonth = getCurrentMonth();
      if (newDateRange.startDate !== currentMonth.startDate || 
          newDateRange.endDate !== currentMonth.endDate) {
        setPlanLimitDialog({
          type: 'PERIOD_FILTER',
          title: '期間変更機能の制限',
          message: '期間変更機能はスタンダードプラン以上で利用可能です。今月のデータのみ表示できます。',
          action: 'upgrade'
        });
        return;
      }
    }
    
    setDateRange(newDateRange);
  };

  // プラン制限ダイアログを閉じる
  const handleClosePlanLimitDialog = () => {
    setPlanLimitDialog(null);
  };

  // プラン変更画面への遷移
  const handleUpgrade = () => {
    setPlanLimitDialog(null);
    window.open('/settings/plan', '_blank');
  };

  // 期間判定ヘルパー関数
  const getCurrentPeriodType = () => {
    const now = new Date();
    const currentMonth = getCurrentMonth();
    
    if (dateRange.startDate === currentMonth.startDate && 
        dateRange.endDate === currentMonth.endDate) {
      return 'thisMonth';
    }
    
    const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const lastMonthPeriod = {
      startDate: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
      endDate: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${new Date(lastMonthYear, lastMonth + 1, 0).getDate()}`
    };
    
    if (dateRange.startDate === lastMonthPeriod.startDate && 
        dateRange.endDate === lastMonthPeriod.endDate) {
      return 'lastMonth';
    }
    
    return 'custom';
  };

  // 期間表示フォーマット
  const formatSelectedPeriod = () => {
    const periodType = getCurrentPeriodType();
    
    switch (periodType) {
      case 'thisMonth':
        const now = new Date();
        return `${now.getFullYear()}年${now.getMonth() + 1}月（今月）`;
      case 'lastMonth':
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return `${lastMonth.getFullYear()}年${lastMonth.getMonth() + 1}月（先月）`;
      default:
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        
        // 同じ月かチェック
        if (startDate.getFullYear() === endDate.getFullYear() && 
            startDate.getMonth() === endDate.getMonth()) {
          return `${startDate.getFullYear()}年${startDate.getMonth() + 1}月`;
        }
        
        return `${startDate.getFullYear()}年${startDate.getMonth() + 1}月 〜 ${endDate.getFullYear()}年${endDate.getMonth() + 1}月`;
    }
  };

  // データが1つの月のみかチェック
  const isSingleMonthData = () => {
    return analysisData?.monthlyData?.length === 1;
  };

  const isCurrentMonthSelected = () => {
    return getCurrentPeriodType() === 'thisMonth';
  };

  // 🔧 修正：チャート情報を取得（selectedDateRangeを追加）
  const chartInfo = analysisData ? AnalysisChart({
    analysisData,
    chartType,
    selectedClient,
    isCurrentMonthSelected: isCurrentMonthSelected(),
    selectedDateRange: dateRange // 🔧 追加：実際の選択期間を渡す
  }) : null;

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ pt: 3, pb: 8, minHeight: 'calc(100vh - 64px)' }}>
        {/* ヘッダーセクション */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              製品分析ダッシュボード
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            製品別の月次供給量分析と顧客別比較
          </Typography>
        </Paper>

        {/* フリープラン制限表示（簡潔版） */}
        {userPlan?.id === 1 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                <strong>フリープラン</strong>：今月の全体データのみ表示
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => window.open('/settings/plan', '_blank')}
              >
                機能制限を解除
              </Button>
            </Box>
          </Alert>
        )}

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 単月選択時の情報表示（修正版） */}
        {isSingleMonthData() && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>単月表示中</strong><br />
              {isCurrentMonthSelected() ? '今月実績' : '選択月実績'} vs 過去1年平均の比較グラフを表示しています。
            </Typography>
          </Alert>
        )}

        {/* 製品・顧客選択とコントロール */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              {/* 製品選択 */}
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedProduct}
                  onChange={(event, newValue) => setSelectedProduct(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="分析対象製品"
                      placeholder="製品を選択..."
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CategoryIcon color="primary" />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            単位: {option.unit}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  noOptionsText="製品が見つかりません"
                />
              </Grid>

              {/* 顧客選択（制限対応版・UI崩れ防止） */}
              <Grid item xs={12} md={3}>
                <Box sx={{ position: 'relative' }}>
                  <ClientAutocomplete
                    selectedClient={selectedClient}
                    onClientChange={handleClientChange}
                    label="顧客フィルター"
                    placeholder={userPlan?.id === 1 ? "制限機能です" : "顧客名で検索（任意）..."}
                    required={false}
                    disabled={!selectedProduct || userPlan?.id === 1}
                    minSearchLength={2}
                    maxResults={20}
                    enableCache={true}
                    sx={{ width: '100%' }}
                  />
                  {userPlan?.id === 1 && (
                    <LockIcon 
                      fontSize="small" 
                      color="disabled" 
                      sx={{ 
                        position: 'absolute', 
                        top: '50%',
                        right: 40,
                        transform: 'translateY(-50%)',
                        zIndex: 1
                      }} 
                    />
                  )}
                </Box>
              </Grid>
              
              {/* グラフ種別選択 */}
              <Grid item xs={12} md={3}>
                <ToggleButtonGroup
                  value={chartType}
                  exclusive
                  onChange={(event, newType) => newType && setChartType(newType)}
                  aria-label="chart type"
                  size="large"
                  disabled={isSingleMonthData()}
                >
                  <ToggleButton value="bar" aria-label="bar chart">
                    <BarChartIcon />
                  </ToggleButton>
                  <ToggleButton value="line" aria-label="line chart">
                    <ShowChartIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
                {isSingleMonthData() && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    ※ 単月データのため比較表示
                  </Typography>
                )}
              </Grid>
              
              {/* 更新ボタンと選択状態 */}
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {selectedProduct && (
                    <Chip
                      icon={<TrendingUpIcon />}
                      label={selectedProduct.name}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {selectedClient && (
                    <Chip
                      icon={<PersonIcon />}
                      label={selectedClient.name}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      onDelete={() => setSelectedClient(null)}
                    />
                  )}
                  <Tooltip title="データを更新">
                    <span>
                      <IconButton 
                        onClick={handleRefresh} 
                        disabled={refreshing || !selectedProduct}
                      >
                        <RefreshIcon sx={{ 
                          animation: refreshing ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 期間選択コンポーネント（制限対応版） */}
        <AnalysisPeriodSelector
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          selectedProduct={selectedProduct}
          selectedClient={selectedClient}
          getCurrentPeriodType={getCurrentPeriodType}
          formatSelectedPeriod={formatSelectedPeriod}
          isCurrentMonthSelected={isCurrentMonthSelected}
          disabled={userPlan?.id === 1} // フリープランでは期間選択を無効化
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : analysisData ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* KPI カード */}
            <Grid item xs={12}>
              <AnalysisKPICards 
                analysisData={analysisData}
                selectedClient={selectedClient}
                isCurrentMonthSelected={isCurrentMonthSelected()}
              />
            </Grid>

            {/* メインチャート */}
            <Grid item xs={12} lg={selectedClient ? 12 : 8}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
                    {chartInfo?.title || '月別供給量推移'}
                  </Typography>
                  {chartInfo?.mainChart}
                </CardContent>
              </Card>
            </Grid>

            {/* 顧客別内訳 - 顧客フィルター時は非表示 */}
            {!selectedClient && (
              <Grid item xs={12} lg={4}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
                      顧客別供給量（上位10社）
                    </Typography>
                    {chartInfo?.clientChart}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              製品を選択してください
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              分析対象の製品を選択すると、供給量データが表示されます
            </Typography>
          </Box>
        )}

        {/* プラン制限ダイアログ */}
        <Dialog 
          open={!!planLimitDialog} 
          onClose={handleClosePlanLimitDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UpgradeIcon color="warning" />
            {planLimitDialog?.title}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              {planLimitDialog?.message}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              データ分析機能の全機能を利用するには、スタンダードプラン（月額3,300円・税込）へのアップグレードをご検討ください。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePlanLimitDialog}>
              閉じる
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleUpgrade}
            >
              プランを確認
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ProductAnalysisDashboard;